<template>
  <div ref="so-simple-table" v-bind="$attrs">
    <v-table
      :class="{
        'so-simple-table--border rounded': outlined,
        'so-simple-table--equal-width-columns': equalWidthColumns,
      }"
      density="compact"
    >
      <thead v-if="!isPivoted">
        <tr>
          <th
            v-for="(header, i) in headers"
            :key="`simple-table-header-${i}`"
            :class="{ 'no-divider': outlined }"
            data-testid="so-simple-table-header-top"
            class="text-left"
            scope="col"
          >
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody data-testid="so-simple-table-body">
        <tr v-if="$slots.tableTop">
          <td class="no-divider" colspan="100%">
            <slot name="tableTop" />
          </td>
        </tr>
        <tr
          v-for="(row, rowIndex) in rows"
          :key="`simple-table-row-${rowIndex}`"
        >
          <th
            v-if="isPivoted"
            :class="{ 'no-divider d-flex align-center': outlined }"
            data-testid="so-simple-table-header-left"
            class="text-left"
            scope="row"
          >
            {{ headers[rowIndex] }}
          </th>
          <td
            v-for="(cell, cellIndex) in row"
            :key="`simple-table-cell-${cellIndex}`"
            :class="{ 'no-divider': outlined }"
            class="text-wrap-pre"
          >
            <SoLink
              v-if="typeof cell === 'object' && cell.type === RowType.LINK"
              data-testid="so-simple-table-link"
              v-bind="cell"
              @click="$emit('click:row', row)"
            />
            <span v-else>{{ cell }}</span>
            <SoCopyToClipboard
              v-if="showCopyToClipboardOnRow(headers[rowIndex], cell)"
              :plain-text="cell as string"
              inline
            />
          </td>
        </tr>
      </tbody>
      <slot name="tableContent" />
    </v-table>
  </div>
  <div class="d-flex mt-1">
    <v-spacer />
    <slot name="actions" />
    <SoCopyToClipboard
      v-if="showCopyToClipboard"
      :element-to-copy="SoSimpleTable"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import {
    SoSimpleTableHeaders,
    SoSimpleTableRow,
    SoSimpleTableRowType as RowType,
  } from "@/types/components/SoSimpleTable"
  import SoCopyToClipboard, {
    CopyToClipboardElement,
  } from "./SoCopyToClipboard.vue"
  import SoLink from "./SoLink.vue"

  export default defineComponent({
    components: {
      SoCopyToClipboard,
      SoLink,
    },
    inheritAttrs: false,
    props: {
      headers: {
        type: Array as PropType<SoSimpleTableHeaders>,
        default: () => [],
      },
      items: {
        type: Array as PropType<SoSimpleTableRow[]>,
        default: () => [],
      },
      isPivoted: {
        type: Boolean,
        default: false,
      },
      outlined: {
        type: Boolean,
        default: false,
      },
      showCopyToClipboard: {
        type: Boolean,
        default: false,
      },
      equalWidthColumns: {
        type: Boolean,
        default: false,
      },
      copyToClipboardFields: {
        type: Array<string>,
        default: () => [],
      },
    },
    emits: ["click:row"],
    data() {
      return {
        SoSimpleTable: null as CopyToClipboardElement,
        RowType,
      }
    },
    computed: {
      rows(): SoSimpleTableRow[] {
        return this.isPivoted ? this.transposeItems(this.items) : this.items
      },
    },
    mounted() {
      this.SoSimpleTable = this.$refs["so-simple-table"] as HTMLElement
    },
    methods: {
      transposeItems(items: SoSimpleTableRow[]): SoSimpleTableRow[] {
        if (!items.length) return []
        const M = items.length
        const N = items[0].length

        // create a new NxM array
        const rotatedItems = Array(N)
          .fill(null)
          .map(() => Array(M).fill(null))

        // rotate items
        for (let i = 0; i < N; i++) {
          for (let j = 0; j < M; j++) {
            rotatedItems[i][j] = items[M - j - 1][i]
          }
        }
        return rotatedItems
      },
      showCopyToClipboardOnRow(field, cell) {
        return (
          this.copyToClipboardFields.includes(field) && !!cell && cell !== "-"
        )
      },
    },
  })
</script>

<style scoped>
  .so-simple-table--border {
    border: 1px solid rgba(var(--v-theme-on-surface), 0.33);
    padding: 8px 0;
  }

  .so-simple-table--equal-width-columns :deep(.v-table__wrapper > table) {
    table-layout: fixed;
  }

  .no-divider {
    border-bottom: none !important;
    min-height: 28px !important;
    height: auto !important;
  }

  .no-divider.text-wrap-pre {
    word-break: break-word;
  }
</style>
